import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { PageHeader, EmptyState } from "@/components/Shared";
import api, { formatApiError } from "@/lib/api";
import { TL, formatDate, todayISO, CARI_TYPE_LABEL, TXN_KIND_LABEL } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";

function CariTxnForm({ cariId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: todayISO(), direction: "alacak", amount: "", description: "", category: "" });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/cari-hareketler", {
        cari_id: cariId,
        date: form.date,
        direction: form.direction,
        amount: parseFloat(form.amount),
        description: form.description,
        category: form.category,
      });
      toast.success("Hareket eklendi");
      setOpen(false);
      setForm({ date: todayISO(), direction: "alacak", amount: "", description: "", category: "" });
      onCreated?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-cari-txn-button">
          <Plus className="w-4 h-4 mr-2" /> Cari Hareketi
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Cari Hareketi Ekle</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tarih</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="cari-txn-date" />
            </div>
            <div className="space-y-2">
              <Label>Tür</Label>
              <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                <SelectTrigger data-testid="cari-txn-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="alacak">Alacak (bize borçlanır)</SelectItem>
                  <SelectItem value="borc">Borç (biz borçlanırız)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Tutar (₺)</Label>
              <Input required type="number" step="0.01" min="0.01" value={form.amount}
                     onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="cari-txn-amount" />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Açıklama</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cari-txn-desc" />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Kategori</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="cari-txn-category" placeholder="Tur, Otel, Transfer..." />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="cari-txn-submit">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CariDetailPage() {
  const { id } = useParams();
  const [cari, setCari] = useState(null);
  const [items, setItems] = useState([]);

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get(`/cariler/${id}`),
      api.get(`/cariler/${id}/hareketler`),
    ]);
    setCari(a.data);
    setItems(b.data);
  };
  useEffect(() => { load().catch(console.error); }, [id]);

  const remove = async (item) => {
    if (!window.confirm("Hareketi silmek istediğinize emin misiniz?")) return;
    try {
      if (item.type === "cari") await api.delete(`/cari-hareketler/${item.id}`);
      else await api.delete(`/hesap-hareketler/${item.id}`);
      toast.success("Silindi");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!cari) return <Layout><div className="text-sm text-foreground/60">Yükleniyor...</div></Layout>;

  return (
    <Layout>
      <Link to="/cariler" className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-foreground mb-4" data-testid="back-to-cariler">
        <ArrowLeft className="w-4 h-4" /> Cari Listesi
      </Link>

      <PageHeader
        title={cari.name}
        subtitle={`${CARI_TYPE_LABEL[cari.type]} • ${cari.phone || "—"} • ${cari.email || "—"}`}
        action={<CariTxnForm cariId={id} onCreated={load} />}
        testId="cari-detail-header"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="border border-border rounded-md p-5 bg-background" data-testid="cari-balance-card">
          <div className="text-[11px] uppercase tracking-wider text-foreground/50">Güncel Bakiye</div>
          <div className={`font-heading text-3xl font-medium tracking-tight mt-2 tabular-nums ${cari.balance > 0 ? "text-[hsl(var(--success))]" : cari.balance < 0 ? "text-destructive" : ""}`}>
            {TL(Math.abs(cari.balance))}
          </div>
          <div className="text-xs text-foreground/60 mt-1">
            {cari.balance > 0 ? "Bizden Alacaklı" : cari.balance < 0 ? "Bize Borçlu" : "Bakiye Yok"}
          </div>
        </div>
        <div className="border border-border rounded-md p-5 bg-background md:col-span-2">
          <div className="text-[11px] uppercase tracking-wider text-foreground/50 mb-2">Adres & Notlar</div>
          <div className="text-sm text-foreground/80">{cari.address || "—"}</div>
          {cari.tax_number && <div className="text-xs text-foreground/60 mt-2">Vergi No: {cari.tax_number}</div>}
          {cari.notes && <div className="text-xs text-foreground/60 mt-2 whitespace-pre-wrap">{cari.notes}</div>}
        </div>
      </div>

      <div className="border border-border rounded-md bg-background overflow-hidden" data-testid="cari-txn-table">
        {items.length === 0 ? (
          <EmptyState title="Henüz hareket yok" hint="Cari veya kasa/banka üzerinden hareket ekleyin." testId="cari-txn-empty" />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-foreground/70">
              <tr>
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Tarih</th>
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Tür</th>
                <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wider">Açıklama</th>
                <th className="text-right py-3 px-4 text-[11px] uppercase tracking-wider">Tutar</th>
                <th className="text-right py-3 px-4 text-[11px] uppercase tracking-wider">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => {
                const isCredit = (t.type === "cari" && t.direction === "alacak") || (t.type === "hesap" && t.kind === "odeme");
                const label = t.type === "cari"
                  ? (t.direction === "alacak" ? "Alacak" : "Borç")
                  : TXN_KIND_LABEL[t.kind] || t.kind;
                return (
                  <tr key={`${t.type}-${t.id}`} className="border-t border-border">
                    <td className="py-3 px-4 text-foreground/80">{formatDate(t.date)}</td>
                    <td className="py-3 px-4"><span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-sm text-xs border border-border">{label}</span></td>
                    <td className="py-3 px-4 text-foreground/80">{t.description || "—"}</td>
                    <td className={`py-3 px-4 text-right tabular-nums font-medium ${isCredit ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
                      {isCredit ? "+" : "−"} {TL(t.amount)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button onClick={() => remove(t)} className="text-foreground/40 hover:text-destructive transition-colors" data-testid={`delete-txn-${t.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
